import React, { useState } from 'react';
import {
    ButtonPrimary,
    Alert,
    Icon
} from '../../components/ComponentLibrary';
import { sendSystemNotification } from '../../services/api';
import {
    Bell,
    Send,
    Users,
    FileText,
    Tag,
    Megaphone,
    AlertTriangle,
    Trophy,
    BookOpen,
    Clock
} from 'lucide-react';
import './Admin.css';

export default function SendNotifications() {
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        targetAudience: 'all', // all, students, admins
        type: 'announcement'
    });
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus({ type: '', message: '' });

        try {
            if (!formData.title || !formData.message) {
                setStatus({ type: 'error', message: 'Please provide both a title and message body.' });
                setLoading(false);
                return;
            }

            const response = await sendSystemNotification(formData);

            if (response.success) {
                setStatus({ type: 'success', message: `Broadcast successfully deployed to ${response.count || 'all designated'} recipients!` });
                setFormData({
                    title: '',
                    message: '',
                    targetAudience: 'all',
                    type: 'announcement'
                });
            } else {
                setStatus({ type: 'error', message: 'The broadcast module failed to deploy.' });
            }
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.message || 'System error detected during broadcast.' });
        } finally {
            setLoading(false);
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'system': return <AlertTriangle size={16} />;
            case 'achievement': return <Trophy size={16} />;
            case 'lesson': return <BookOpen size={16} />;
            default: return <Megaphone size={16} />;
        }
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case 'system': return 'System Alert';
            case 'achievement': return 'Achievement';
            case 'lesson': return 'Educational';
            default: return 'Announcement';
        }
    };

    return (
        <>
            <header className="admin-page-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                    <div style={{ width: '60px', height: '60px', borderRadius: '1rem', background: '#0f172a', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Bell size={28} />
                    </div>
                    <div>
                        <h1>Broadcasting Center</h1>
                        <p>Engage the user base through real-time push protocols</p>
                    </div>
                </div>
            </header>

            {status.message && (
                <div style={{ marginBottom: '2rem' }}>
                    <Alert variant={status.type} onClose={() => setStatus({ type: '', message: '' })}>
                        {status.message}
                    </Alert>
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '8fr 5fr', gap: '2rem', alignItems: 'start' }}>
                {/* Input Matrix */}
                <div className="admin-table-section-card" style={{ padding: '2.5rem' }}>
                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                        <div>
                            <label className="label-caps" style={{ marginBottom: '0.75rem', display: 'block' }}>Broadcast Designation</label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="Execution title..."
                                style={{ width: '100%', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
                                required
                            />
                        </div>

                        <div>
                            <label className="label-caps" style={{ marginBottom: '0.75rem', display: 'block' }}>Message Payload</label>
                            <textarea
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                placeholder="Enter content payload..."
                                style={{ width: '100%', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0', minHeight: '180px' }}
                                required
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                            <div>
                                <label className="label-caps" style={{ marginBottom: '0.75rem', display: 'block' }}>Target Segment</label>
                                <select
                                    name="targetAudience"
                                    value={formData.targetAudience}
                                    onChange={handleChange}
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
                                >
                                    <option value="all">Universal (All)</option>
                                    <option value="students">Segment: Students</option>
                                    <option value="admins">Segment: Staff</option>
                                </select>
                            </div>

                            <div>
                                <label className="label-caps" style={{ marginBottom: '0.75rem', display: 'block' }}>Protocol Type</label>
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
                                >
                                    <option value="announcement">Announcement</option>
                                    <option value="system">Critical Alert</option>
                                    <option value="lesson">Educational Update</option>
                                    <option value="achievement">Metric Achievement</option>
                                </select>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'end', paddingTop: '1.5rem', borderTop: '1px solid #f1f5f9' }}>
                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-action primary"
                                style={{ padding: '1rem 2.5rem' }}
                            >
                                {loading ? 'Processing Broadcast...' : 'Execute Deployment'}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Live Preview */}
                <div style={{ position: 'sticky', top: '2.5rem' }}>
                    <p className="label-caps" style={{ textAlign: 'center', marginBottom: '1rem' }}>Interface Approximation</p>
                    <div className="admin-table-section-card" style={{ padding: '2.5rem', border: '1px solid #e2e8f0', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1.5rem' }}>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
                                {formData.title || 'Broadcast Title'}
                            </h3>
                            <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8' }}>JUST NOW</span>
                        </div>

                        <p style={{ fontSize: '1rem', color: '#64748b', lineHeight: 1.6, marginBottom: '2rem' }}>
                            {formData.message || 'Payload visualization will materialize here once the input fields are populated...'}
                        </p>

                        <span className={`status-pill ${formData.type === 'system' ? 'status-inactive' : formData.type === 'achievement' ? 'status-pending' : 'status-active'}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                            {getTypeIcon(formData.type)}
                            {getTypeLabel(formData.type).toUpperCase()}
                        </span>
                    </div>

                    <div style={{ marginTop: '1.5rem', padding: '1.5rem', background: '#0f172a', borderRadius: '1rem', color: 'white' }}>
                        <p style={{ fontSize: '0.875rem', opacity: 0.9, lineHeight: 1.5 }}>
                            <strong>Optimization Parameter:</strong> Ensure headlines are concise for maximum retention on viewport notifications.
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
}
